<script setup>
import { ref, watch, toRefs } from 'vue';
import { onMounted } from '@vue/runtime-core';
import { definePreset, updatePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import { useCoin2CoreStore } from '@/stores/coin2core';
import TimeoutModal from '@/components/modals/TimeoutModal.vue';
import SecondaryAuthenticationModal from '@/components/modals/SecondaryAuthenticationModal.vue';

const store = useCoin2CoreStore();
const loading = ref(false);
const startupFailed = ref(false);
let config = ref(null);

// Applies the DB-driven theme (GET portalconfiguration/theme/{portalTypeId}):
// `semantic` feeds the PrimeVue preset; `custom` carries the portal's CSS variable
// contract, written onto <html> so it overrides the compiled theme-daland-light.css
// fallback. When no DB theme exists the compiled fallback stands on its own.
function applyPortalTheme() {
  const theme = store.portalTheme;
  if (!theme || !Object.keys(theme).length) {
    return;
  }

  const { custom, ...preset } = theme;

  updatePreset(definePreset(Aura, preset));

  if (custom) {
    for (const [name, value] of Object.entries(custom)) {
      document.documentElement.style.setProperty(name, value);
    }
  }
}

async function initApp() {
  loading.value = true;
  startupFailed.value = false;

  const timeout = setTimeout(() => {
    startupFailed.value = true;
  }, 15000);

  try {
    await store.startSession();
    let resp = await store.getConfiguration();

    applyPortalTheme();

    await store.getCryptoCurrencies();
    clearTimeout(timeout);
    loading.value = false;
  } catch (e) {
    clearTimeout(timeout);
    startupFailed.value = true;
  }
}

onMounted(() => {
  initApp();
});

</script>

<template>
<body>
  <RouterView />
  <Toast position="top-center" />
  <TimeoutModal />
  <SecondaryAuthenticationModal />

  <!-- Loading overlay - blocks interaction until startup completes -->
  <Transition name="loading-fade">
    <div v-if="loading" class="startup-overlay">
      <div v-if="!startupFailed" class="startup-spinner-container">
        <ProgressSpinner class="startup-spinner" />
      </div>
      <div v-else class="startup-error-container">
        <i class="pi pi-exclamation-circle startup-error-icon" />
        <h3 class="startup-error-title">We're having trouble connecting</h3>
        <p class="startup-error-message">Something didn't load correctly. Please try again.</p>
        <button class="startup-retry-button" @click="initApp">
          <i class="pi pi-refresh" /> Try Again
        </button>
      </div>
    </div>
  </Transition>
</body>
</template>

<style scoped>
/* Startup loading overlay - covers everything, blocks interaction */
.startup-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--gradient-bg, #f4f6fa);
  display: flex;
  align-items: center;
  justify-content: center;
}

.startup-spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border-radius: 1rem;
  background: var(--card-bg, rgba(255, 255, 255, 0.85));
  backdrop-filter: blur(16px);
  border: 1px solid var(--card-border, #e3e6ee);
  box-shadow: 0 8px 32px rgba(36, 19, 68, 0.08);
}

:deep(.startup-spinner) {
  width: 2.5rem !important;
  height: 2.5rem !important;
}

/* Startup error state */
.startup-error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem;
  border-radius: 1.5rem;
  background: var(--card-bg, rgba(255, 255, 255, 0.85));
  backdrop-filter: blur(16px);
  border: 1px solid var(--card-border, #e3e6ee);
  box-shadow: 0 8px 32px rgba(36, 19, 68, 0.08);
  max-width: 400px;
}

.startup-error-icon {
  font-size: 2.5rem;
  color: var(--warning-color, #f59e0b);
  margin-bottom: 1rem;
}

.startup-error-title {
  color: var(--text-primary, #1e2433);
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.startup-error-message {
  color: var(--text-secondary, #5a6072);
  margin: 0 0 1.5rem 0;
  font-size: 0.9rem;
}

.startup-retry-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.75rem;
  background: var(--gradient-primary, linear-gradient(135deg, #f7931a, #ffa12c));
  color: var(--primary-contrast, #241344);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(247, 147, 26, 0.3);
}

.startup-retry-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(247, 147, 26, 0.4);
}

/* Fade-out transition when loading completes */
.loading-fade-leave-active {
  transition: opacity 0.4s ease;
}

.loading-fade-leave-to {
  opacity: 0;
}
</style>
